import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError, extractErrorMessage } from "@/api/client";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { DataTable, type Column } from "@/components/layout/DataTable";
import {
  MATERIAL_AUDIENCES,
  portalMaterialsApi,
  type MaterialAudience,
  type PortalMaterialDto,
} from "@/api/portalMaterials";

// Portal Materials Library — admin landing. Audience filter + list,
// upload dialog, edit metadata dialog, replace file, download, delete.

const QUERY_KEY = ["admin", "portal-materials"] as const;

export function PortalMaterialsAdminPage() {
  // Round-3 #7 — External Collection › Portal Materials.
  useBreadcrumbs([{ label: "External Collection" }, { label: "Portal Materials" }]);
  const { hasPermission } = useAuth();
  const canManage = hasPermission("PortalMaterials.Manage");
  const toast = useToast();
  const qc = useQueryClient();

  const [audienceFilter, setAudienceFilter] = useState<MaterialAudience | "">("");
  const [includeInactive, setIncludeInactive] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PortalMaterialDto | null>(null);

  const listQ = useQuery({
    queryKey: [...QUERY_KEY, audienceFilter || null, includeInactive],
    queryFn: () =>
      portalMaterialsApi.list({
        includeInactive,
        audience: audienceFilter || null,
      }),
    enabled: canManage,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: QUERY_KEY });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => portalMaterialsApi.delete(id),
    onSuccess: () => {
      toast.success("Material deleted.");
      refresh();
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  const onDownload = async (m: PortalMaterialDto) => {
    try {
      const dl = await portalMaterialsApi.download(m.id);
      window.open(dl.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  if (!canManage) {
    return (
      <div className="text-small text-status-error">
        You do not have permission to manage portal materials.
      </div>
    );
  }

  const columns: Column<PortalMaterialDto>[] = [
    {
      key: "title",
      header: "Title",
      render: (m) => (
        <div className="min-w-0">
          <div className="font-semibold text-onyx truncate">{m.title}</div>
          {m.description && (
            <div className="text-caption text-gray-mid truncate max-w-md">
              {m.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "audience",
      header: "Audience",
      render: (m) => <span className="text-small text-onyx">{m.audience}</span>,
    },
    {
      key: "category",
      header: "Category",
      render: (m) => (
        <span className="text-small text-onyx">{m.category ?? "—"}</span>
      ),
    },
    {
      key: "file",
      header: "File",
      render: (m) => (
        <span className="text-caption text-gray-mid truncate inline-block max-w-[14rem]">
          {m.originalFileName}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (m) => (
        <span
          className={
            m.active
              ? "text-caption text-status-success"
              : "text-caption text-gray-mid"
          }
        >
          {m.active ? "Active" : "Retired"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (m) => (
        <div className="flex items-center gap-space-1 justify-end">
          <button
            type="button"
            onClick={() => onDownload(m)}
            className="p-space-1 rounded-md text-gold-shadow hover:bg-white-warm"
            aria-label={`Download ${m.title}`}
          >
            <Download size={16} aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => setEditTarget(m)}
            className="p-space-1 rounded-md text-gold-shadow hover:bg-white-warm"
            aria-label={`Edit ${m.title}`}
          >
            <Pencil size={16} aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => {
              if (
                window.confirm(
                  `Delete '${m.title}'? This permanently removes the file.`,
                )
              ) {
                deleteMutation.mutate(m.id);
              }
            }}
            disabled={deleteMutation.isPending}
            className="p-space-1 rounded-md text-gray-mid hover:text-status-error hover:bg-white-warm"
            aria-label={`Delete ${m.title}`}
          >
            <Trash2 size={16} aria-hidden />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Admin · Portal"
        title="Materials library"
        description="Downloads served to portal users, each tagged with the one audience it is for."
        actions={
          <div className="flex items-center gap-space-2">
            <Button type="button" variant="secondary" onClick={refresh}>
              <RefreshCw size={16} aria-hidden /> Refresh
            </Button>
            <Button type="button" onClick={() => setUploadOpen(true)}>
              <Plus size={16} aria-hidden /> Upload material
            </Button>
          </div>
        }
      />

      <div className="flex items-end gap-space-3 mb-space-4">
        <div>
          {/* Step 6 W6 — a material targets exactly ONE audience, unlike a
              collection template, which targets a set. Same enum, different
              shape; the label says which one this is. */}
          <label
            htmlFor="pm-audience"
            className="font-display text-label font-semibold uppercase tracking-label text-gray-mid block mb-space-1"
          >
            Audience
          </label>
          <Select
            id="pm-audience"
            value={audienceFilter}
            onChange={(e) =>
              setAudienceFilter(e.target.value as MaterialAudience | "")
            }
            className="w-48"
          >
            <option value="">All audiences</option>
            {MATERIAL_AUDIENCES.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </Select>
        </div>
        <label className="flex items-center gap-space-2 text-small text-onyx pb-space-2">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Show retired
        </label>
      </div>

      <DataTable
        columns={columns}
        rows={listQ.data ?? []}
        loading={listQ.isLoading}
        error={listQ.isError ? (listQ.error as Error).message : null}
        emptyMessage="No materials yet. Click 'Upload material' to add one."
        rowKey={(m) => m.id}
      />

      {uploadOpen && (
        <UploadDialog
          onClose={() => setUploadOpen(false)}
          onSaved={() => {
            setUploadOpen(false);
            refresh();
          }}
        />
      )}

      {editTarget && (
        <EditDialog
          target={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

interface UploadDialogProps {
  onClose: () => void;
  onSaved: () => void;
}

function UploadDialog({ onClose, onSaved }: UploadDialogProps) {
  const toast = useToast();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [audience, setAudience] = useState<MaterialAudience>("Broker");
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const uploadMutation = useMutation({
    mutationFn: () =>
      file
        ? portalMaterialsApi.upload(file, {
            title: title.trim(),
            description: description.trim() || null,
            category: category.trim() || null,
            audience,
          })
        : Promise.reject(new Error("File is required.")),
    onSuccess: () => {
      toast.success("Material uploaded.");
      onSaved();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.errors.length) setErrors(err.errors);
      else setErrors([extractErrorMessage(err)]);
    },
  });

  const submit = () => {
    setErrors([]);
    const issues: string[] = [];
    if (!title.trim()) issues.push("Title is required.");
    if (!file) issues.push("File is required.");
    if (issues.length) {
      setErrors(issues);
      return;
    }
    uploadMutation.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Upload material"
      size="md"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={uploadMutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={submit} loading={uploadMutation.isPending}>
            Upload
          </Button>
        </>
      }
    >
      <div className="space-y-space-3">
        <FormField label="Title" required htmlFor="pm-title">
          <Input
            id="pm-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            autoFocus
          />
        </FormField>
        <FormField label="Description" htmlFor="pm-description">
          <Input
            id="pm-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
          />
        </FormField>
        <FormField label="Category" htmlFor="pm-category" helperText="Optional grouping (e.g. 'Pitch decks').">
          <Input
            id="pm-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            maxLength={100}
          />
        </FormField>
        <FormField
          label="Audience"
          required
          htmlFor="pm-audience-input"
          helperText="One audience per material. Choose All to serve every portal."
        >
          <Select
            id="pm-audience-input"
            value={audience}
            onChange={(e) => setAudience(e.target.value as MaterialAudience)}
          >
            {MATERIAL_AUDIENCES.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="File" required htmlFor="pm-file">
          <input
            id="pm-file"
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-small"
          />
        </FormField>
        {errors.length > 0 && (
          <ul
            className="list-disc pl-space-4 text-caption text-status-error"
            role="alert"
          >
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

interface EditDialogProps {
  target: PortalMaterialDto;
  onClose: () => void;
  onSaved: () => void;
}

function EditDialog({ target, onClose, onSaved }: EditDialogProps) {
  const toast = useToast();
  const [title, setTitle] = useState(target.title);
  const [description, setDescription] = useState(target.description ?? "");
  const [category, setCategory] = useState(target.category ?? "");
  const [audience, setAudience] = useState<MaterialAudience>(target.audience);
  const [active, setActive] = useState(target.active);
  const [replacementFile, setReplacementFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      await portalMaterialsApi.update(target.id, {
        title: title.trim(),
        description: description.trim() || null,
        category: category.trim() || null,
        audience,
        active,
      });
      if (replacementFile) {
        await portalMaterialsApi.replaceFile(target.id, replacementFile);
      }
    },
    onSuccess: () => {
      toast.success("Material updated.");
      onSaved();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.errors.length) setErrors(err.errors);
      else setErrors([extractErrorMessage(err)]);
    },
  });

  const submit = () => {
    setErrors([]);
    if (!title.trim()) {
      setErrors(["Title is required."]);
      return;
    }
    updateMutation.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Edit ${target.title}`}
      size="md"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={submit} loading={updateMutation.isPending}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="space-y-space-3">
        <FormField label="Title" required htmlFor="pm-edit-title">
          <Input
            id="pm-edit-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
          />
        </FormField>
        <FormField label="Description" htmlFor="pm-edit-description">
          <Input
            id="pm-edit-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
          />
        </FormField>
        <FormField label="Category" htmlFor="pm-edit-category">
          <Input
            id="pm-edit-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            maxLength={100}
          />
        </FormField>
        <FormField
          label="Audience"
          required
          htmlFor="pm-edit-audience"
          helperText="One audience per material. Choose All to serve every portal."
        >
          <Select
            id="pm-edit-audience"
            value={audience}
            onChange={(e) => setAudience(e.target.value as MaterialAudience)}
          >
            {MATERIAL_AUDIENCES.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </Select>
        </FormField>
        <label className="flex items-center gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          Active (visible in the portal)
        </label>
        <FormField
          label="Replace file"
          htmlFor="pm-edit-file"
          helperText={`Current: ${target.originalFileName}. Leave blank to keep.`}
        >
          <input
            id="pm-edit-file"
            type="file"
            onChange={(e) => setReplacementFile(e.target.files?.[0] ?? null)}
            className="block w-full text-small"
          />
        </FormField>
        {errors.length > 0 && (
          <ul
            className="list-disc pl-space-4 text-caption text-status-error"
            role="alert"
          >
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
